import {
    accountSelector,
    authTokenSelector, deepCloneObject, Form,
    FormControlChangeType,
    FormControlType,
    IFormConfig, isNotNullOrUndefined,
    isNullOrUndefined, Loader
} from 'educat-common-web';
import React from 'react';
import {connect} from 'react-redux';
import {BehaviorSubject, Subscription} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {TutoringSubject} from '../../../../../api/provider/educatTutoringScopeTypeAPI';
import {fixInjectedProperties, lazyInject} from '../../../../../ioc';
import {
    IMentorRegistrationService,
    MentorRegistrationSteps,
    MentorTutoringScopeIds
} from '../../../../../service/mentorRegistrationService';
import {RootState} from '../../../../../store/reducers';
import {IAlertManagerService} from "../../../../../service/alertManagerService";
import {
    tutoringSubjectScopeGroup
} from "./subjectClassesFormConfig";
import {
    classesFormGroup,
    mentorClassesFormConfig
} from "./mentorClassesFormConfig";

interface IConnectedFormStepClassesProps {
    readonly authToken: string;
    readonly account: any;
}

interface IExternalFormStepClassesProps {
    readonly submitStep?: (
        stepName: MentorRegistrationSteps,
        stepValue: any,
        tutoringScopesIds: { maturityExamId: string | null; examId: string | null; otherId: string | null }
    ) => void;
    readonly prevStep?: (step: MentorRegistrationSteps, stepValue?: any, prevStep?: MentorRegistrationSteps) => void;
    readonly tutoringScopesList: { multiselectOptions: { [key: string]: any }[]; subjectsArray: TutoringSubject[] };
    readonly rawFormData?: any;
    readonly stepData?: any;
    readonly changeCurrentStep?: () => void;
    readonly mentor?: any;
    readonly onFormUpdate: any;
    readonly updateValidation: any;
}

interface IFormStepClassesProps extends IConnectedFormStepClassesProps, IExternalFormStepClassesProps {
}

interface IFormStepClassesState {
    isStepValid: boolean;
    isProcessing: boolean;
    stepValue: any;
    stepName: MentorRegistrationSteps;
    formConfig: typeof IFormConfig;
    mentorTutoringScopesId: MentorTutoringScopeIds;
}

class FormClasses extends React.Component<IFormStepClassesProps, IFormStepClassesState> {
    readonly subscriptions: Subscription[] = [];
    readonly onValueStateChange$: BehaviorSubject<any> = new BehaviorSubject(null);
    @lazyInject('AlertManagerService') private alertManager: IAlertManagerService | undefined;
    @lazyInject('MentorRegistrationService') private mentorRegistrationService: IMentorRegistrationService;

    constructor(props: IFormStepClassesProps) {
        super(props);

        this.state = {
            isStepValid: false,
            isProcessing: false,
            formConfig: mentorClassesFormConfig,
            stepValue: null,
            stepName: MentorRegistrationSteps.CLASSES,
            mentorTutoringScopesId: {maturityExamId: null, examId: null, otherId: null},
        };

        fixInjectedProperties(this);
    }

    componentDidMount() {
        this.subscriptions.push(
            this.onValueStateChange$
                .pipe(
                    tap((data: any) => this.props.onFormUpdate(data?.value, 'classes')),
                    filter((data: any) => data && data.changeType === FormControlChangeType.User),
                    tap((data: any) => this.onFormValueChange(data.value))
                )
                .subscribe()
        );


        let updatedFormConfig = deepCloneObject(this.state.formConfig);

        updatedFormConfig.controls.push(classesFormGroup(this.props.tutoringScopesList.multiselectOptions, this.props.mentor));

        const values = this.props.mentor.tutoringScopes;
        if (values.length > 0) {
            const subjectId = values[0].subject.id;
            this.showSelectedSubjectFormConfig(subjectId, values, updatedFormConfig);
        }

        this.setState({formConfig: updatedFormConfig, stepValue: this.state.stepValue});
    }


    componentWillUnmount() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    render() {
        return (
            <>
                <Loader showLoader={this.state.isProcessing}/>
                <div className="onboarding-form-wrapper">
                    <Form
                        touched
                        config={this.state.formConfig}
                        controlName={MentorRegistrationSteps.CLASSES}
                        onValueStateChange={this.onValueStateChange}
                        onValidationStateChange={this.formValidityChange}
                        value={this.state.stepValue}
                    />
                </div>
            </>
        );
    }

    private onValueStateChange = (controlName: string, value: any, changeType: typeof FormControlChangeType) => {
        this.onValueStateChange$.next({controlName: controlName, value: value, changeType: changeType});
    };

    private onFormValueChange = (value: any) => {
        const update: any = {formConfig: Object.assign({}, this.state.formConfig), stepValue: value};

        Object.keys(value).forEach((key: string) => {
            if (key !== 'classes' || isNullOrUndefined(value[key])) {
                return;
            }
            update.formConfig.controls.forEach((control: any) => {
                if (!control.hasOwnProperty('controls')) {
                    return;
                }

                Object.keys(control.controls).forEach((controlKey: string) => {
                    if (controlKey === 'subject') {
                        control.controls[controlKey].hostClass =
                            value[key].subjectClasses === 'yes' ? 'col-xl-12 form-row' : 'col-xl-12 form-row d-none';

                        if (isNotNullOrUndefined(value[key].subject)) {
                            const subjectValue = value[key].subject.value ? value[key].subject.value : value[key].subject;

                            const mentorTutoringScopesId = this.showSelectedSubjectFormConfig(subjectValue, update.stepValue, update.formConfig);

                            if (isNotNullOrUndefined(mentorTutoringScopesId)) {
                                update.mentorTutoringScopesId = mentorTutoringScopesId;
                            }
                        }
                    }

                    if (controlKey === 'subjectClasses' && value[key].subjectClasses === 'no') {
                        this.removeSelectedSubjectFormConfig(update.formConfig, update.stepValue);
                    }
                });
            });
        });

        this.setState(update);

        this.props.onFormUpdate(this.state.stepValue, 'classes');
    };

    private formValidityChange = (controlName: string, isValid: boolean) => {
        if (this.state.isStepValid !== isValid) {
            this.setState({isStepValid: isValid});
            this.props.updateValidation(isValid);
        }
    };

    private showSelectedSubjectFormConfig(selectedSubjectId: string | null, value: any, config: any) {
        // if (!value.classes) {
        //     return;
        // }
        const selected = this.props.tutoringScopesList.subjectsArray.find((subject: TutoringSubject) => subject.id === selectedSubjectId);

        if (config.controls.length > 1) {
            config.controls = config.controls.splice(0, 1);
            // updatedFormConfig.controls[0].controls.subject.hostClass =
            // 	this.state.stepValue.classes.subjectClasses === "yes" ? "col-xl-12 form-row" : "col-xl-12 form-row d-none";
        }

        if (!selected) {
            return {
                maturityExamId: null,
                examId: null,
                otherId: null,
            };
        }

        const mentorMaturityExams = this.props.mentor.tutoringScopes.find((scope: any)=>{return scope.name === 'matura'});
        const mentorExams = this.props.mentor.tutoringScopes.find((scope: any)=>{return scope.name === 'egzaminy'});
        const mentorOthers = this.props.mentor.tutoringScopes.find((scope: any)=>{return scope.name === 'pozostałe'});

        if (selected.maturityExamArray.length > 0) {
            const controls = this.createFormControlsFromScope(selected.maturityExamArray, mentorMaturityExams);
            config.controls.push(
                tutoringSubjectScopeGroup('maturityExams', 'mentorOnboarding.registration.classes.formControls.maturityExam.title', controls)
            );
        }
        if (selected.examArray.length > 0 && selected.examId) {
            const controls = this.createFormControlsFromScope(selected.examArray, mentorExams);
            config.controls.push(
                tutoringSubjectScopeGroup('exams', 'mentorOnboarding.registration.classes.formControls.exams.title', controls)
            );
        }
        if (selected.otherArray.length > 0 && selected.otherId) {
            const controls = this.createFormControlsFromScope(selected.otherArray, mentorOthers);
            config.controls.push(
                tutoringSubjectScopeGroup('others', 'mentorOnboarding.registration.classes.formControls.additional.title', controls)
            );
        }

        return {
            maturityExamId: selected.maturityExamId ? selected.maturityExamId : null,
            examId: selected.examId ? selected.examId : null,
            otherId: selected.otherId ? selected.otherId : null,
        };
    }

    private removeSelectedSubjectFormConfig = (config: any, value: any) => {
        const updatedControls = config.controls.filter((item: any) => item.key === 'classes');
        config['controls'] = [...updatedControls];
        delete value?.classes?.subject;
    };

    private createFormControlsFromScope(scopeItemArray: { name: string; id: string }[], value?: any) {
        let controls = {};
        scopeItemArray.forEach((scopeItem: { name: string; id: string }) => {
            const scopeOffered = !!(value?.tutoringScopeItems.filter((scope:any)=> scope.tutoringScopeItemType.id === scopeItem.id).length);
            controls = Object.assign(controls, {
                [scopeItem.id]: {
                    controlType: 'control',
                    defaultValue: scopeOffered,
                    value: scopeOffered,
                    formControlType: FormControlType.CHECKBOX,
                    validationRules: [],
                    placeholder: '',
                    label: scopeItem.name,
                    isLabelHidden: false,
                    isCheckboxLabelRaw: false,
                    checkboxLabel: ``,
                    hostClass: 'checkbox-fieldset-container',
                },
            });
        });
        return controls;
    }

}

export default connect(
    (state: RootState) => ({
        account: accountSelector(state),
        authToken: authTokenSelector(state),
    }),
    {}
)(FormClasses);
